import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { Address } from 'viem';

type TShortenAddress = (
  address?: Address | string | null,
  chars?: number,
) => string;
export const shortenAddress: TShortenAddress = (address, chars = 5) => {
  if (address) {
    return `${address.slice(0, chars + 2)}...${address.slice(-chars)}`;
  } else {
    return '';
  }
};
export const buildIPFSUrl = (hash: string) => {
  return `https://ipfs.io/ipfs/${hash}`;
};
interface CopyToClipboardProps {
  text: string | null | undefined;
}
type TCopyToClipboard = (text: CopyToClipboardProps['text']) => void;
export const copyToClipboard: TCopyToClipboard = (text) => {
  if (!text) return;
  if (navigator?.clipboard) {
    navigator.clipboard.writeText(text);
  } else {
    const el: HTMLTextAreaElement = document.createElement('textarea');
    el.value = text;
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
  }

  toast.success('Copied', {
    id: 'clipboard',
    icon: '✅',
    position: 'top-right',
  });
};
interface FormatUnitsToAmountProps {
  string: string | number | bigint | null | undefined;
  decimalToken?: number;
}

interface FormatEthersToAmountProps {
  string: string | number | bigint | null | undefined;
}

const MAX_TOKEN_DECIMALS = 36;

/**
 * `ethers.formatUnits` only accepts an integer base-unit value: a fractional
 * string ("554.75000000"), exponential notation ("1e18") or anything
 * non-numeric makes it throw `invalid BigNumberish string`. Amounts reaching
 * the UI come from the API and are not guaranteed to be clean, and a throw
 * here happens during render, which unmounts the whole tree (blank screen).
 * Sanitize instead: unparseable input renders as 0.
 */
const toBaseUnits = (value: string | number | bigint): string => {
  const parsed = new BigNumber(value.toString().trim());
  if (!parsed.isFinite()) return '0';
  // Base units have no fractional part; truncate rather than reject.
  return parsed.integerValue(BigNumber.ROUND_DOWN).toFixed(0);
};

const toTokenDecimals = (decimals: number | undefined, fallback: number) => {
  if (typeof decimals !== 'number' || !Number.isFinite(decimals)) {
    return fallback;
  }
  return Math.min(Math.max(Math.trunc(decimals), 0), MAX_TOKEN_DECIMALS);
};

export const formatUnitsToAmount = ({
  string,
  decimalToken = 6,
}: FormatUnitsToAmountProps): string => {
  return ethers.formatUnits(
    toBaseUnits(string ?? '0'),
    toTokenDecimals(decimalToken, 6),
  );
};

export const formatEthersToAmount = ({
  string,
}: FormatEthersToAmountProps): string => {
  return ethers.formatEther(toBaseUnits(string ?? '0'));
};

export const formatSensitiveCardNumber = (cardNumber?: string | null) => {
  if (!cardNumber) return 'xxxx xxxx xxxx xxxx';
  // Remove any existing spaces
  const cleanNumber = cardNumber.replace(/\s/g, '');
  return `**** **** **** ${cleanNumber}`;
};

export const formatCardNumber = (cardNumber?: string) => {
  if (!cardNumber || cardNumber.includes('unde')) return 'xxxx xxxx xxxx xxxx';
  // Remove any existing spaces and format with groups of 4
  const cleanNumber = cardNumber.replace(/\s/g, '');
  return cleanNumber.replace(/(.{4})/g, '$1 ').trim();
};
export const formatExpiryDate = (expiryDate?: string) => {
  if (!expiryDate) return 'x/xx';
  // Assuming expiryDate is in format like "2025-12" or "12/25"
  if (expiryDate.includes('-')) {
    const [year, month] = expiryDate.split('-');
    return `${month}/${year.slice(-2)}`;
  }
  return expiryDate;
};

export const getCurrencySymbol = (
  locale: string = 'en-US',
  currencyCode: string,
): string => {
  try {
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0, // Set to 0 to avoid displaying decimal places in the symbol extraction
      maximumFractionDigits: 0,
    });
    const parts = formatter.formatToParts(0); // Format a zero value to get the parts
    const currencyPart = parts.find((part) => part.type === 'currency');
    return currencyPart ? currencyPart.value : '$'; // Default to $ if symbol not found
  } catch (error) {
    console.error('Error getting currency symbol:', error);
    return '$'; // Default fallback
  }
};
